import { useEffect, useRef } from "react";
import { METRICS } from "@/lib/domains";

const Sparkline = ({ data, color, frozen }) => {
  if (!data || data.length < 2) return null;
  const w = 100;
  const h = 28;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const pts = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * w;
      const y = h - ((v - min) / span) * h;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-7 opacity-90">
      <polyline
        points={pts}
        fill="none"
        stroke={frozen ? "#3B82F6" : color}
        strokeWidth="1.6"
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

const MetricCard = ({ metric, value, spark, range, frozen }) => {
  const ref = useRef(null);
  const prev = useRef(value);
  useEffect(() => {
    if (ref.current && prev.current !== value && !frozen) {
      ref.current.classList.remove("value-tick");
      void ref.current.offsetWidth;
      ref.current.classList.add("value-tick");
    }
    prev.current = value;
  }, [value, frozen]);

  const inRange = value >= range[0] && value <= range[1];
  const color = frozen ? "#3B82F6" : inRange ? "#22d3ee" : "#f59e0b";

  return (
    <div
      data-testid={metric.testid}
      className="rounded-xl border border-slate-800 bg-slate-900/50 p-4 flex flex-col justify-between"
    >
      <div className="flex items-center justify-between">
        <span className="mono text-[10px] uppercase tracking-widest text-slate-400">
          {metric.label}
        </span>
        <span
          className={`h-1.5 w-1.5 rounded-full ${
            frozen ? "bg-blue-400" : inRange ? "bg-emerald-400 beacon" : "bg-amber-400"
          }`}
        />
      </div>
      <div className="mt-2 flex items-end gap-1.5">
        <span
          ref={ref}
          className="mono text-3xl font-extrabold tracking-tight tabular-nums"
          style={{ color }}
        >
          {value.toFixed(metric.decimals)}
        </span>
        {metric.unit && (
          <span className="mono text-[11px] text-slate-500 mb-1">{metric.unit}</span>
        )}
      </div>
      <div className="mt-2">
        <Sparkline data={spark} color="#22d3ee" frozen={frozen} />
      </div>
      <div className="mt-1 mono text-[9px] text-slate-600 tracking-wide">
        RANGE {range[0]}–{range[1]}
      </div>
    </div>
  );
};

export const TelemetryFeed = ({ telemetry, sparks, domain, frozen }) => {
  return (
    <div className="glass-panel rounded-2xl p-5 h-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="mono text-xs font-semibold uppercase tracking-widest text-cyan-400">
          Telemetry Feed
        </h3>
        <span className="mono text-[10px] text-slate-500">
          {frozen ? "◼ FROZEN · LAMINAR" : "● LIVE · 2s"}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {METRICS.map((m) => (
          <MetricCard
            key={m.key}
            metric={m}
            value={telemetry[m.key]}
            spark={sparks[m.key]}
            range={domain.ranges[m.key]}
            frozen={frozen}
          />
        ))}
      </div>
    </div>
  );
};
